import { Injectable } from '@angular/core';
import Typo from 'typo-js';

/**
 * Production-Ready Spell Check Service
 * Integrates with Typo.js (Hunspell) for comprehensive spell checking
 * with 100,000+ word dictionary support
 */
@Injectable({
  providedIn: 'root'
})
export class SpellCheckService {
  private dictionary: Typo | null = null;
  private dictionaryLoaded = false;
  private dictionaryLoadError = false;
  private customLegalTerms: Set<string> = new Set();
  private userDictionary: Set<string> = new Set();

  constructor() {
    this.initializeDictionary();
  }

  /**
   * Initialize the spell check dictionary
   * Loads Hunspell dictionary files and custom legal terms
   */
  private async initializeDictionary(): Promise<void> {
    try {
      console.log('Loading spell check dictionary...');
      
      // Load the dictionary files
      const affData = await this.loadFile('assets/dictionaries/en_US.aff');
      const dicData = await this.loadFile('assets/dictionaries/en_US.dic');
      
      // Initialize Typo.js dictionary
      this.dictionary = new Typo('en_US', affData, dicData);
      
      // Load custom legal terms
      await this.loadCustomLegalTerms();
      
      this.dictionaryLoaded = true;
      console.log('Spell check dictionary loaded successfully (100,000+ words)');
    } catch (error) {
      console.error('Failed to load spell check dictionary:', error);
      this.dictionaryLoadError = true;
      this.dictionaryLoaded = false;
    }
  }

  /**
   * Load custom legal terminology from file
   * These terms are specific to legal domain and won't be in standard dictionaries
   */
  private async loadCustomLegalTerms(): Promise<void> {
    try {
      const legalTerms = await this.loadFile('assets/dictionaries/legal-terms.txt');
      const terms = legalTerms.split('\n').map(t => t.trim()).filter(t => t.length > 0);
      terms.forEach(term => this.customLegalTerms.add(term.toLowerCase()));
      console.log(`Loaded ${this.customLegalTerms.size} custom legal terms`);
    } catch (error) {
      console.warn('Failed to load custom legal terms (non-critical):', error);
    }
  }

  /**
   * Load a file from the assets folder
   * @param path - The path to the file relative to the application root
   * @returns Promise resolving to the file content as a string
   */
  private loadFile(path: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', path, true);
      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.responseText);
        } else {
          reject(new Error(`Failed to load ${path}: ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error(`Network error loading ${path}`));
      xhr.send();
    });
  }

  /**
   * Check if the dictionary is ready for use
   * @returns true if dictionary is loaded and ready
   */
  isDictionaryReady(): boolean {
    return this.dictionaryLoaded && !this.dictionaryLoadError;
  }

  /**
   * Check if a word is misspelled
   * @param word - The word to check
   * @returns true if the word is misspelled, false otherwise
   */
  isMisspelled(word: string): boolean {
    if (!word || word.trim().length === 0) {
      return false;
    }

    // Return false if dictionary not loaded yet or failed to load
    if (!this.isDictionaryReady()) {
      return false;
    }

    const cleanWord = word.trim();
    const lowerWord = cleanWord.toLowerCase();

    // Ignore numbers and alphanumeric combinations
    if (/^\d+$/.test(cleanWord) || /^\w*\d\w*$/.test(cleanWord)) {
      return false;
    }

    // Ignore very short words (likely abbreviations or acronyms)
    if (cleanWord.length <= 2) {
      return false;
    }

    // Ignore all uppercase words (likely acronyms)
    if (cleanWord === cleanWord.toUpperCase() && cleanWord.length <= 5) {
      return false;
    }

    // Check if it's in user's custom dictionary
    if (this.userDictionary.has(lowerWord)) {
      return false;
    }

    // Check if it's in custom legal terms
    if (this.customLegalTerms.has(lowerWord)) {
      return false;
    }

    // Check against main Hunspell dictionary
    // Typo.js returns true if word is correct, so we negate it
    try {
      if (!this.dictionary) {
        return false;
      }
      const isCorrect = this.dictionary.check(cleanWord);
      return !isCorrect;
    } catch (error) {
      console.warn('Error checking word:', word, error);
      return false;
    }
  }

  /**
   * Get spelling suggestions for a misspelled word
   * Uses hybrid approach: Hunspell + custom candidate generation
   * @param word - The misspelled word
   * @returns An array of suggested corrections ranked by relevance
   */
  getSuggestions(word: string): string[] {
    if (!word || word.trim().length === 0) {
      return [];
    }

    // Return empty array if dictionary not loaded
    if (!this.isDictionaryReady()) {
      return [];
    }

    const cleanWord = word.trim();

    // Don't suggest for numbers or very short words
    if (/^\d+$/.test(cleanWord) || cleanWord.length <= 2) {
      return [];
    }

    try {
      if (!this.dictionary) {
        return [];
      }
      
      // Get suggestions from Typo.js (uses Hunspell algorithm)
      let hunspellSuggestions = this.dictionary.suggest(cleanWord) || [];
      
      // Generate additional candidates using edit distance
      const customCandidates = this.generateCandidates(cleanWord);
      
      // Combine and deduplicate suggestions
      const allSuggestions = [...new Set([...hunspellSuggestions, ...customCandidates])];
      
      if (allSuggestions.length === 0) {
        return [];
      }

      // Rank all suggestions by Levenshtein distance
      const rankedSuggestions = allSuggestions
        .map((suggestion: string) => ({
          word: suggestion,
          distance: this.levenshteinDistance(cleanWord.toLowerCase(), suggestion.toLowerCase())
        }))
        .sort((a, b) => {
          // Sort by distance, then alphabetically for same distance
          if (a.distance === b.distance) {
            return a.word.localeCompare(b.word);
          }
          return a.distance - b.distance;
        })
        .map(item => item.word);
      
      // Preserve case if original word was capitalized
      const isCapitalized = cleanWord[0] === cleanWord[0].toUpperCase();
      const processedSuggestions = rankedSuggestions.map((suggestion: string) => {
        if (isCapitalized && suggestion.length > 0) {
          return suggestion[0].toUpperCase() + suggestion.slice(1).toLowerCase();
        }
        return suggestion;
      });
      
      // Return top 8 suggestions for better coverage
      return processedSuggestions.slice(0, 8);
    } catch (error) {
      console.warn('Error getting suggestions for word:', word, error);
      return [];
    }
  }

  /**
   * Generate candidate corrections using edit operations
   * Includes: insertions, deletions, substitutions, and transpositions
   * @param word - The misspelled word
   * @returns Array of valid word candidates
   */
  private generateCandidates(word: string): string[] {
    if (!this.dictionary) {
      return [];
    }

    const candidates: Set<string> = new Set();
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    const lowerWord = word.toLowerCase();

    try {
      // 1. Deletions - remove each character
      for (let i = 0; i < word.length; i++) {
        const candidate = lowerWord.slice(0, i) + lowerWord.slice(i + 1);
        if (candidate.length > 0 && this.isValidWord(candidate)) {
          candidates.add(candidate);
        }
      }

      // 2. Insertions - insert each letter at each position
      for (let i = 0; i <= word.length; i++) {
        for (const char of alphabet) {
          const candidate = lowerWord.slice(0, i) + char + lowerWord.slice(i);
          if (this.isValidWord(candidate)) {
            candidates.add(candidate);
          }
        }
      }

      // 3. Substitutions - replace each character
      for (let i = 0; i < word.length; i++) {
        for (const char of alphabet) {
          if (char !== lowerWord[i]) {
            const candidate = lowerWord.slice(0, i) + char + lowerWord.slice(i + 1);
            if (this.isValidWord(candidate)) {
              candidates.add(candidate);
            }
          }
        }
      }

      // 4. Transpositions - swap adjacent characters
      for (let i = 0; i < word.length - 1; i++) {
        const candidate = lowerWord.slice(0, i) + lowerWord[i + 1] + lowerWord[i] + lowerWord.slice(i + 2);
        if (this.isValidWord(candidate)) {
          candidates.add(candidate);
        }
      }
    } catch (error) {
      console.warn('Error generating candidates:', error);
    }

    return Array.from(candidates);
  }

  /**
   * Check if a word is valid (exists in dictionary)
   * @param word - The word to check
   * @returns true if word exists in dictionary
   */
  private isValidWord(word: string): boolean {
    try {
      return this.dictionary?.check(word) || false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Calculate Levenshtein distance between two strings
   * Used for ranking suggestions by similarity to the misspelled word
   * @param str1 - First string
   * @param str2 - Second string
   * @returns The edit distance between the two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const len1 = str1.length;
    const len2 = str2.length;
    const matrix: number[][] = [];

    // Initialize matrix
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }

    // Fill matrix
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }

    return matrix[len1][len2];
  }

  /**
   * Add a word to the user's custom dictionary (runtime only)
   * This allows users to add project-specific terms
   * @param word - The word to add to the dictionary
   */
  addToCustomDictionary(word: string): void {
    if (word && word.trim().length > 0) {
      this.userDictionary.add(word.toLowerCase().trim());
      console.log(`Added "${word}" to custom dictionary`);
    }
  }

  /**
   * Remove a word from the user's custom dictionary
   * @param word - The word to remove
   */
  removeFromCustomDictionary(word: string): void {
    if (word && word.trim().length > 0) {
      this.userDictionary.delete(word.toLowerCase().trim());
    }
  }

  /**
   * Check if a word is in the user's custom dictionary
   * @param word - The word to check
   * @returns true if the word is in the custom dictionary
   */
  isInCustomDictionary(word: string): boolean {
    return this.userDictionary.has(word.toLowerCase().trim());
  }

  /**
   * Get all words in the user's custom dictionary
   * @returns Array of custom dictionary words
   */
  getCustomDictionary(): string[] {
    return Array.from(this.userDictionary);
  }

  /**
   * Clear the user's custom dictionary
   */
  clearCustomDictionary(): void {
    this.userDictionary.clear();
  }

  /**
   * Check if dictionary is currently loading
   * @returns true if loading, false otherwise
   */
  isLoading(): boolean {
    return !this.dictionaryLoaded && !this.dictionaryLoadError;
  }

  /**
   * Check if there was an error loading the dictionary
   * @returns true if there was a load error
   */
  hasLoadError(): boolean {
    return this.dictionaryLoadError;
  }

  /**
   * Get dictionary statistics
   * @returns Object containing dictionary stats
   */
  getStats(): { loaded: boolean; error: boolean; customTerms: number; userWords: number } {
    return {
      loaded: this.dictionaryLoaded,
      error: this.dictionaryLoadError,
      customTerms: this.customLegalTerms.size,
      userWords: this.userDictionary.size
    };
  }
}
